import { useCallback, useState } from 'react';

import { PublicClientApplication } from '@azure/msal-browser';
import { Typography, Stack, useTheme, Fade, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { ActionButton, ActionButtonStyle, InputField, SkopeiConnectLogo } from '~components';
import VectorImage from '~components/media/vectorImage';
import { login } from '~constants';
import { StatusVariant } from '~enums';
import { Dimens } from '~themes';

interface LoginFormProps {
	onSignInWithEmail: (email: string) => void;
	logInError?: string;
	isLoading: boolean;
}

const LoginForm = ({ onSignInWithEmail, logInError = '', isLoading }: LoginFormProps) => {
	const { palette } = useTheme();
	const { t } = useTranslation();
	const [email, setEmail] = useState('');
	const handleSubmit = () => {
		if (!isLoading) {
			onSignInWithEmail(email);
		}
	};
	const tenant = 'skopeiIdentity';

	const msalInstance = new PublicClientApplication({
		auth: {
			clientId: import.meta.env.VITE_SKOPEI_OIDC_CLIENT_ID,
			authority: import.meta.env.VITE_SKOPEI_OIDC_AUTHORITY_URL,
			redirectUri: window.location.origin + '/auth/signin-callback-oidc',
			knownAuthorities: [`${tenant}.ciamlogin.com`],
		},
		cache: {
			cacheLocation: 'localStorage',
		},
	});

	// Clears the msalInstance form any non finished sign in attempts.
	const validateInProgressSession = useCallback(async () => {
		await msalInstance.initialize();
		await msalInstance.handleRedirectPromise();
	}, [msalInstance]);
	validateInProgressSession();

	const signInWithMicrosoft = async () => {
		const loginRequest = {
			scopes: [...login.signInScopesArray],
			extraQueryParameters: {
				domainHint: 'Microsoft',
				idp: 'MicrosoftAccount',
				prompt: 'select_account',
			},
		};

		await msalInstance.initialize();
		msalInstance.loginRedirect(loginRequest);
	};

	const signInWithGoogle = async () => {
		const loginRequest = {
			scopes: [...login.signInScopesArray],
			extraQueryParameters: {
				domain_hint: 'Google',
				prompt: 'login',
			},
		};

		await msalInstance.initialize();
		msalInstance.loginRedirect(loginRequest);
	};

	const signInWithApple = async () => {
		const loginRequest = {
			scopes: [...login.signInScopesArray],
			extraQueryParameters: {
				domain_hint: 'Apple',
				prompt: 'login',
			},
		};

		await msalInstance.initialize();
		msalInstance.loginRedirect(loginRequest);
	};

	return (
		<Stack
			justifyContent='center'
			sx={{
				width: {
					xs: '100%',
					sm: '360px',
				},
				padding: {
					xs: Dimens.spacing2xl,
					md: 0,
				},
			}}
			gap={Dimens.spacing3xl}
		>
			<SkopeiConnectLogo />
			<Stack gap={Dimens.spacingMd}>
				<Typography variant='heading' color={palette.textPrimary.main} component='h1'>
					{t('logIn', { ns: 'auth' })}
				</Typography>
				<Typography variant='paragraph' color={palette.textSecondary.main} component='p'>
					{t('logInWelcome', { ns: 'auth' })}
				</Typography>
			</Stack>
			<form
				onSubmit={(e) => {
					e.preventDefault();
					handleSubmit();
				}}
			>
				<Stack>
					<Stack gap={Dimens.spacingMd}>
						<Stack gap={Dimens.spacingXs}>
							<InputField
								label={t('user.fields.email', { ns: 'entities' })}
								placeholder={t('emailPlaceholder', { ns: 'system' })}
								StartIcon='icon/mail'
								inputType='email'
								value={email}
								autofocus
								onChange={(newValue: string) => {
									setEmail(newValue);
								}}
							/>
							<Fade in={logInError.length > 0} appear={false}>
								<Typography variant='paragraphSm' color={palette.textError.main}>
									{/* Falls back to 'error' because otherwise the component wont get any height */}
									{logInError.length > 0 ? logInError : 'error'}
								</Typography>
							</Fade>
						</Stack>

						<ActionButton
							variant={StatusVariant.Brand}
							label={t('continue', { ns: 'actions' })}
							loading={isLoading}
							onClick={(e) => {
								e.preventDefault();
								handleSubmit();
							}}
						/>
					</Stack>
					<Stack
						direction='row'
						gap={Dimens.spacingMd}
						alignItems='center'
						sx={{ marginY: Dimens.spacingXl, height: Dimens.spacing3xl }}
					>
						<Box
							sx={{
								height: Dimens.spacing3xs,
								width: '100%',
								background: palette.borderTertiary.main,
							}}
						/>
						<Typography variant='labelXs' color={palette.textTertiary.main}>
							or
						</Typography>
						<Box
							sx={{
								height: Dimens.spacing3xs,
								width: '100%',
								background: palette.borderTertiary.main,
							}}
						/>
					</Stack>

					<Stack gap={Dimens.spacingLg}>
						<ActionButton
							style={ActionButtonStyle.Bordered}
							label={t('signInWithMicrosoft', { ns: 'actions' })}
							startIcon={<VectorImage name='logo/microsoft' />}
							onClick={async (e) => {
								e.preventDefault();
								signInWithMicrosoft();
							}}
						/>
						<ActionButton
							style={ActionButtonStyle.Bordered}
							label={t('signInWithGoogle', { ns: 'actions' })}
							startIcon={<VectorImage name='logo/google' />}
							onClick={async (e) => {
								e.preventDefault();
								signInWithGoogle();
							}}
						/>
						<ActionButton
							style={ActionButtonStyle.Bordered}
							label={t('signInWithApple', { ns: 'actions' })}
							startIcon={<VectorImage name='logo/apple' />}
							onClick={async (e) => {
								e.preventDefault();
								signInWithApple();
							}}
						/>
					</Stack>
				</Stack>
			</form>
			<Stack
				sx={{
					width: '100%',
					alignSelf: 'center',
				}}
				alignContent='center'
			>
				<Typography
					variant='paragraphSm'
					color={palette.textSecondary.main}
					component='p'
					textAlign='center'
				>
					{t('logInDisclaimer', { ns: 'auth' })}
				</Typography>
				<Stack direction='row' justifyContent='center' gap={Dimens.spacingXs}>
					<Typography variant='paragraphSm' color={palette.textPrimary.main} component='p'>
						<a
							href='https://skopei.com/algemene-voorwaarden'
							target='_blank'
							style={{ textDecoration: 'none' }}
						>
							<Typography
								variant='paragraphSm'
								color={palette.textPrimary.main}
								component='span'
								sx={{
									fontWeight: 500,
									'&:hover': {
										textDecoration: 'underline',
									},
								}}
							>
								{t('termsOfService', { ns: 'auth' })}
							</Typography>
						</a>
						{` ${t('and', { ns: 'system' })} `}
						<a
							href='https://skopei.com/privacystatement-skopei-app'
							target='_blank'
							style={{ textDecoration: 'none' }}
						>
							<Typography
								variant='paragraphSm'
								color={palette.textPrimary.main}
								component='span'
								sx={{
									fontWeight: 500,
									'&:hover': {
										textDecoration: 'underline',
									},
								}}
							>
								{t('privacyPolicy', { ns: 'auth' })}
							</Typography>
						</a>
					</Typography>
				</Stack>
			</Stack>
		</Stack>
	);
};

export default LoginForm;
